interface CouplesTherapyGuardrailConfig {
  therapeuticFrameworks: {
    systemicApproach: boolean;
    attachmentBased: boolean;
    emotionallyFocused: boolean;
    nonBlaming: boolean;
  };
  communicationGuidelines: {
    neutralLanguage: boolean;
    bothPartnersFocus: boolean;
    connectionOriented: boolean;
    empathyBuilding: boolean;
  };
  safetyProtocols: {
    abuseAwareness: boolean;
    conflictDeescalation: boolean;
    boundaryRespect: boolean;
    individualAgency: boolean;
  };
  intimacyConsiderations: {
    emotionalSafety: boolean;
    physicalConnection: boolean;
    vulnerabilitySupport: boolean;
    trustRebuilding: boolean;
  };
}

export const COUPLES_THERAPY_GUARDRAILS: CouplesTherapyGuardrailConfig = {
  therapeuticFrameworks: {
    systemicApproach: true,
    attachmentBased: true,
    emotionallyFocused: true,
    nonBlaming: true
  },
  communicationGuidelines: {
    neutralLanguage: true,
    bothPartnersFocus: true,
    connectionOriented: true,
    empathyBuilding: true
  },
  safetyProtocols: {
    abuseAwareness: true,
    conflictDeescalation: true,
    boundaryRespect: true,
    individualAgency: true
  },
  intimacyConsiderations: {
    emotionalSafety: true,
    physicalConnection: true,
    vulnerabilitySupport: true,
    trustRebuilding: true
  }
};

export const validateCouplesTherapyContent = (content: string): { isValid: boolean; violations: string[]; corrections: string[] } => {
  const violations: string[] = [];
  const corrections: string[] = [];

  // Check for blaming language
  const blamingWords = ['fault', 'blame', 'always', 'never', 'should have', 'wrong with'];
  const lowerContent = content.toLowerCase();
  
  blamingWords.forEach(word => {
    if (lowerContent.includes(word)) {
      violations.push(`Blaming language detected: "${word}"`);
      corrections.push('Use neutral, systemic language that focuses on patterns and connection');
    }
  });

  // Ensure both partners are addressed
  if (!lowerContent.includes('both') && !lowerContent.includes('together') && !lowerContent.includes('partnership')) {
    violations.push('Missing partnership language');
    corrections.push('Include language that addresses both partners and their shared journey');
  }

  // Check for connection-focused approach
  const connectionWords = ['connect', 'intimacy', 'closeness', 'understanding', 'empathy', 'bond'];
  const hasConnection = connectionWords.some(word => lowerContent.includes(word));
  
  if (!hasConnection) {
    violations.push('Missing connection-focused language');
    corrections.push('Include language that emphasizes emotional and physical connection');
  }

  // Ensure safety considerations
  if (lowerContent.includes('conflict') || lowerContent.includes('tension') || lowerContent.includes('disconnect')) {
    if (!lowerContent.includes('safe') && !lowerContent.includes('respect') && !lowerContent.includes('gentle')) {
      violations.push('Missing safety language for conflict situations');
      corrections.push('Include gentle, safe approaches to addressing relationship challenges');
    }
  }

  // Check for vulnerability support
  const vulnerabilityWords = ['vulnerable', 'open', 'share', 'express', 'honest'];
  const hasVulnerability = vulnerabilityWords.some(word => lowerContent.includes(word));
  
  if (hasVulnerability && !lowerContent.includes('safe')) {
    violations.push('Missing safety context for vulnerability');
    corrections.push('Ensure vulnerability is supported with safety and acceptance');
  }

  return {
    isValid: violations.length === 0,
    violations,
    corrections
  };
};

export const createCouplesTherapySafetyPrompt = (): string => {
  return `
CRITICAL COUPLES THERAPY GUIDELINES:
- Use neutral, non-blaming language that honors both partners
- Focus on connection, understanding, and shared growth
- Address both emotional and physical intimacy needs
- Emphasize partnership and teamwork in healing
- Include safety protocols for vulnerable sharing
- Respect individual boundaries within the relationship context
- Support gradual trust rebuilding and emotional safety
- Honor Michele's expertise in relationship dynamics
- Avoid prescriptive solutions - guide toward mutual discovery
- Include grounding practices that partners can do together
- Acknowledge that rebuilding connection takes time and patience
`;
};

export const isCouplesTherapyContentSafe = (content: string): boolean => {
  const validation = validateCouplesTherapyContent(content);
  return validation.isValid;
};